// Short audio cues for the microphone: a rising two-note chime when listening
// starts and a falling one when it stops. Generated with Web Audio so there are
// no files to load and they work offline.
//
// playEarcon() resolves when the cue has finished, so callers can open the mic
// after the start cue and keep it out of the recording.

const CUES = {
    start: [587.33, 880.0],   // D5 -> A5
    stop: [880.0, 587.33],    // A5 -> D5
    error: [329.63, 329.63],  // E4, E4
};

const NOTE_SECONDS = 0.085;
const GAP_SECONDS = 0.02;
const PEAK_GAIN = 0.16;

let ctx = null;

function getContext() {
    if (ctx) return ctx;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    try {
        ctx = new Ctor();
    } catch {
        ctx = null;
    }
    return ctx;
}

function vibrate(kind) {
    if (!('vibrate' in navigator)) return;
    try {
        navigator.vibrate(kind === 'start' ? 12 : kind === 'error' ? [30, 40, 30] : 8);
    } catch {
        // Some browsers throw when vibration is blocked; it's only a nicety.
    }
}

/**
 * Play a microphone cue.
 * @param {'start'|'stop'|'error'} kind
 * @returns {Promise<void>} resolves after the cue has played (or immediately if audio is unavailable)
 */
export async function playEarcon(kind) {
    vibrate(kind);
    const notes = CUES[kind];
    const audio = getContext();
    if (!notes || !audio) return;

    try {
        if (audio.state === 'suspended') {
            await audio.resume();
        }
    } catch {
        return;
    }

    const start = audio.currentTime + 0.01;
    const master = audio.createGain();
    master.gain.value = 1;
    master.connect(audio.destination);

    notes.forEach((freq, i) => {
        const t0 = start + i * (NOTE_SECONDS + GAP_SECONDS);
        const t1 = t0 + NOTE_SECONDS;

        const env = audio.createGain();
        env.gain.setValueAtTime(0.0001, t0);
        env.gain.exponentialRampToValueAtTime(PEAK_GAIN, t0 + 0.012);
        env.gain.exponentialRampToValueAtTime(0.0001, t1);
        env.connect(master);

        // A sine plus a quiet octave reads as a soft "bloop" rather than a beep.
        const base = audio.createOscillator();
        base.type = 'sine';
        base.frequency.setValueAtTime(freq, t0);
        base.connect(env);
        base.start(t0);
        base.stop(t1 + 0.02);

        const overtone = audio.createOscillator();
        const overtoneGain = audio.createGain();
        overtoneGain.gain.value = 0.18;
        overtone.type = 'sine';
        overtone.frequency.setValueAtTime(freq * 2, t0);
        overtone.connect(overtoneGain).connect(env);
        overtone.start(t0);
        overtone.stop(t1 + 0.02);
    });

    const totalMs = (notes.length * (NOTE_SECONDS + GAP_SECONDS)) * 1000 + 30;
    await new Promise((resolve) => setTimeout(resolve, totalMs));
    master.disconnect();
}

/** Call from a user gesture early on (e.g. first tap) so iOS lets cues play later. */
export function primeEarcons() {
    const audio = getContext();
    if (audio && audio.state === 'suspended') {
        audio.resume().catch(() => {});
    }
}
